import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { Link } from 'react-router-dom'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { IconTrash, IconWorld, IconFileExport, IconEye } from '@tabler/icons-react'
import { trim, startCase } from 'lodash'

import { PageHeader } from '@/components/headers/PageHeader'
import { AdvancedTable } from '@/components/tables/AdvancedTable/AdvancedTable'
import { Card } from '@/components/cards/Card'
import { AbstractForm } from '@/components/forms/formik/AbstractForm'
import { ButtonAndFormModal } from '@/components/layout/modals/ButtonAndFormModal'
import { toast } from '@/components/toasts/toast.helpers'
import {
  createPlaybook,
  fetchPlaybooks,
  updatePlaybookAgentAppVisibility,
  updatePlaybookLanguage,
} from '@/reducers/playbooks/playbooks.actions'
import { closeModal } from '@/reducers/ui/ui.redux'
import { formatTime } from '@/utils/helpers'
import { truncatedColumn } from '@/components/helpers/tableColumnHelpers'
import { ActionsDropdown } from '@/components/dropdowns/ActionsDropdown'
import { fetchingAPI, apiService } from '@/api'

import { PlaybooksPageModals } from './components/PlaybooksPageModals'

const PlaybooksPageComponent = ({ flags }) => {
  const dispatch = useDispatch()
  const { organizationid: organizationId, edit_config: canEditPlaybooks } = useSelector(
    (state) => state.currentUser
  )
  const organizations = useSelector((state) => state.organizations)
  const { playbooks, loading } = useSelector((state) => state.playbooks)
  const insightEvents = useSelector((state) => state.insightEvents)
  const isBaltoAdmin = organizationId === 1

  const [activeModal, setActiveModal] = useState(null)
  const [activeRow, setActiveRow] = useState(null)

  const openRowModal = (modalType, row) => {
    setActiveModal(modalType)
    setActiveRow(row)
  }

  const closeActiveModal = () => {
    setActiveModal(null)
    setActiveRow(null)
  }

  const handleExportPlaybook = async (playbook) => {
    try {
      const response = await fetchingAPI(
        `${apiService.core}/api/configs/${playbook.id}`,
        'GET',
        dispatch
      )

      const prettyJson = JSON.stringify(response, null, 2)

      const blob = new Blob([prettyJson], { type: 'application/json' })
      const url = URL.createObjectURL(blob)

      const link = document.createElement('a')
      link.href = url
      link.download = `${playbook.name}.json`
      document.body.appendChild(link)
      link.click()

      document.body.removeChild(link)
      URL.revokeObjectURL(url)
    } catch (err) {
      toast.error('Failed to export playbook', { cause: err })
      console.error(err)
    }
  }

  const handleCreatePlaybook = (formValues) => {
    const { name, config_cid, organization_id, copy_notetaker_config } = formValues
    const playbook = { name: trim(name), organization_id }

    dispatch(createPlaybook(playbook, config_cid, insightEvents, copy_notetaker_config))
    dispatch(closeModal())
  }

  const handleUpdateConfigLanguage = (language, cid) => {
    dispatch(updatePlaybookLanguage(language, cid))
  }

  const handlePlaybookAgentAppVisibilityChange = (cid, isVisibleToAgentApp) => {
    dispatch(updatePlaybookAgentAppVisibility(cid, isVisibleToAgentApp))
  }

  const columns = [
    {
      accessor: 'name',
      label: 'Name',
      isSearchable: true,
      format: (playbook, row) => (
        <Link to={`/playbooks/${row.id}/checklist`} className="table-link">
          {truncatedColumn(playbook)}
        </Link>
      ),
    },
    {
      accessor: 'config_agent_app_visibility',
      label: 'Visibility',
      format: (value, row) => {
        const visibilityOptions = [
          {
            label: 'Visible',
            onClick: () => handlePlaybookAgentAppVisibilityChange(row.cid, true),
            testId: 'playbook-visible-option',
          },
          {
            label: 'Hidden',
            onClick: () => handlePlaybookAgentAppVisibilityChange(row.cid, false),
            testId: 'playbook-hidden-option',
          },
        ]
        return (
          <div className="flex-align-center">
            <ActionsDropdown
              triggerLabel={value ? 'Visible' : 'Hidden'}
              buttonProps={{ disabled: !isBaltoAdmin && !canEditPlaybooks }}
              actions={visibilityOptions}
              dataTestId="playbook-visibility-dropdown"
            />
          </div>
        )
      },
    },
    {
      accessor: 'created_on',
      label: 'Last Updated',
      format: (value) => formatTime(value),
    },
  ]

  // allow Balto users and Balto Sales users to set a playbook to "Demo" language
  const canDemoLanguages = organizationId === 1 || organizationId === 17

  if (canDemoLanguages || flags.allSpanishPlaybookToggle2022 || flags.frenchEnabled) {
    const languageColumn = {
      accessor: 'language',
      label: 'Language',
      format: (value, row) => {
        const languageOptions = [
          {
            label: 'English',
            onClick: () => handleUpdateConfigLanguage('english', row.cid),
          },
        ]

        if (flags.allSpanishPlaybookToggle2022) {
          languageOptions.push({
            label: 'Spanish',
            onClick: () => handleUpdateConfigLanguage('spanish', row.cid),
          })
        }

        if (flags.frenchEnabled) {
          languageOptions.push({
            label: 'French',
            onClick: () => handleUpdateConfigLanguage('french', row.cid),
          })
        }

        if (canDemoLanguages) {
          languageOptions.push({
            label: 'Multilingual (Demo Only)',
            onClick: () => handleUpdateConfigLanguage('multilingual', row.cid),
          })
        }

        return (
          <div className="flex-align-center">
            <ActionsDropdown
              triggerLabel={startCase(value)}
              actions={languageOptions}
              dataTestId="playbook-language-dropdown"
            />
          </div>
        )
      },
    }
    columns.splice(1, 0, languageColumn)
  }

  if (isBaltoAdmin) {
    columns.push({
      accessor: 'organization_name',
      label: 'Organization',
      isSearchable: true,
    })
  }

  const actions = [
    {
      label: 'Actions',
      trigger: (row) => {
        const playbookActions = []

        if (isBaltoAdmin) {
          playbookActions.push({
            label: 'Update Playbook Organization',
            icon: <IconWorld />,
            onClick: () => openRowModal('updateOrganization', row),
            testId: 'update-org-playbook-action',
          })
        }

        if (isBaltoAdmin || canEditPlaybooks) {
          playbookActions.push({
            label: 'Export Playbook',
            icon: <IconFileExport />,
            onClick: () => handleExportPlaybook(row),
            testId: 'export-playbook-action',
          })
          playbookActions.push({
            label: 'Visibility Settings',
            icon: <IconEye />,
            onClick: () => openRowModal('visibility', row),
            testId: 'visibility-settings-playbook-action',
          })
          playbookActions.push({
            label: 'Delete Playbook',
            icon: <IconTrash />,
            onClick: () => openRowModal('deletePlaybook', row),
            testId: 'delete-playbook-action',
            isDanger: true,
          })
        }

        return <ActionsDropdown actions={playbookActions} />
      },
    },
  ]

  const rows = playbooks.map((playbook) => {
    const date = playbook.config_created ? new Date(playbook.config_created) : null
    const isValidDate = date instanceof Date && !Number.isNaN(date.getTime())
    return {
      ...playbook,
      created_on: isValidDate ? date.toISOString() : null,
    }
  })

  useEffect(() => {
    dispatch(fetchPlaybooks())
  }, [])

  const schema = [
    { name: 'name', type: 'text', label: 'Name', required: true },
    {
      name: 'config_cid',
      type: 'select',
      label: 'Starting Playbook (optional)',
      options: playbooks.map((playbook) => ({ value: playbook.cid, label: playbook.name })),
    },
    {
      name: 'copy_notetaker_config',
      type: 'checkbox',
      label: 'Copy Notetaker Configuration',
      resetOnDisable: true,
      dependencies: [
        {
          key: 'config_cid',
          operator: 'exists',
        },
      ],
    },
  ]

  if (isBaltoAdmin) {
    schema.push({
      name: 'organization_id',
      type: 'select',
      label: 'Organization',
      isClearable: false,
      required: true,
      options: organizations.map((organization) => ({
        value: organization.id,
        label: organization.name,
      })),
    })
  }

  return (
    <>
      <PageHeader
        data-testid="playbooks-page"
        title="Playbooks"
        actions={
          isBaltoAdmin || canEditPlaybooks ? (
            <ButtonAndFormModal
              buttonLabel="Create Playbook"
              modalTitle="Create Playbook"
              modalId="playbooks/create"
              modalProps={{ size: 'tiny' }}
              buttonProps={{ primary: true }}
              form={
                <AbstractForm
                  schema={schema}
                  onSubmit={handleCreatePlaybook}
                  buttonLabel="Create"
                  onClose={() => dispatch(closeModal())}
                  isModal
                />
              }
            />
          ) : null
        }
      />

      <Card className="no-padding">
        <AdvancedTable
          defaultOrderBy="name"
          loading={loading}
          columns={columns}
          rows={rows}
          actions={isBaltoAdmin || canEditPlaybooks ? actions : []}
          stickyAction
          pagination
        />
      </Card>

      <PlaybooksPageModals
        activeModal={activeModal}
        activeRow={activeRow}
        closeActiveModal={closeActiveModal}
        organizations={organizations}
        playbooks={playbooks}
        isBaltoAdmin={isBaltoAdmin}
        currentUserOrgId={organizationId}
      />
    </>
  )
}

const PlaybooksPage = withLDConsumer()(PlaybooksPageComponent)

export default PlaybooksPage
